import { createSelector } from 'reselect';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import moment from 'moment';
import { fromNow } from 'lib/utils/date';

import { getWorkOrderManagement } from './getWorkOrderManagement';
import { getLatestActivityMode } from './getLatestActivityMode';
import { getLatestActivity } from './getLatestActivity';
import { getHeavyUpdate } from './getUpdate';
import { getOrderProgress } from './getOrderProgress';
import { getHasOpenActivitySet } from './getHasOpenActivitySet';
import { getIsApmERPJobFlow } from './getIsApmERPJobFlow';

const getCurrentWorkOrderOp = createSelector(
  [getWorkOrderManagement, getHasOpenActivitySet, getIsApmERPJobFlow],
  (workOrderManagement, hasOpenActivitySet, isApmERPJobFlow) => {
    // APM flow: current job is set directly by the opdash display MFE
    // (most recent program event or labor ticket). Always use whatever is in Redux.
    if (isApmERPJobFlow) {
      return workOrderManagement?.currentWorkOrderOp ?? null;
    }

    // Removed duplicated NATS ticket-state checks because getHasOpenActivitySet already centralizes them.
    // Legacy + NATS: only expose currentWorkOrderOp when the activity set is open
    // (synthetic set from labor ticket in NATS, Redux set in legacy).
    return hasOpenActivitySet ? workOrderManagement?.currentWorkOrderOp : null;
  }
);

const getCurrentWorkOrderOpTotalGoodParts = createSelector(
  [getCurrentWorkOrderOp],
  (currentWorkOrderOp) => {
    return currentWorkOrderOp?.totalGoodParts;
  }
);

const getUnitDuration = (activitySet, cycleTimeMs) => {
  return (
    moment
      .duration(
        activitySet?.localMedianUnitDuration ||
          activitySet?.expectedUnitDuration ||
          activitySet?.idealMetricDuration
      )
      .asMilliseconds() || cycleTimeMs / activitySet.actualParts
  );
};

const getCurrentWorkOrderOpCompletionDelta = createSelector(
  [
    getCurrentWorkOrderOp,
    getLatestActivitySet,
    getHeavyUpdate,
    getOrderProgress,
  ],
  (
    currentWorkOrderOp,
    latestActivitySet,
    _heavyUpdate, // eslint-disable-line no-unused-vars
    orderProgress
  ) => {
    if (currentWorkOrderOp) {
      const {
        cycleTimeMs,
        scheduledFinishDate,
        finishQuantity,
      } = currentWorkOrderOp;

      // Use real-time parts count (includes both work order and labor scope parts)
      const partsRemaining = Math.max(finishQuantity - orderProgress, 0);
      const unitDuration = getUnitDuration(latestActivitySet, cycleTimeMs);
      const timeToComplete = Math.max(unitDuration * partsRemaining, 0);

      return moment.duration(fromNow(timeToComplete).diff(scheduledFinishDate));
    }
    return null;
  }
);

const getCompletionDeltaText = (completionDelta, isEarly) => {
  if (completionDelta < 1000 * 60) {
    return '✓ On time';
  }

  const formattedDuration = moment
    .duration(completionDelta)
    .format('h[h] m[m] s[s]', {
      largest: 2,
    });

  if (isEarly) {
    return `↑ ${formattedDuration} early`;
  }
  return `↓ ${formattedDuration} late`;
};

const getCurrentWorkOrderOpCompletionDeltaText = createSelector(
  [getCurrentWorkOrderOpCompletionDelta],
  (completionDelta) => {
    const isEarly = completionDelta.asSeconds() <= 0;
    return getCompletionDeltaText(completionDelta, isEarly);
  }
);

const getCurrentWorkOrderOpTimeToComplete = createSelector(
  [
    getCurrentWorkOrderOp,
    getLatestActivitySet,
    getLatestActivityMode,
    getLatestActivity,
    getHeavyUpdate,
    getOrderProgress,
  ],
  (
    currentWorkOrderOp,
    latestActivitySet,
    latestActivityMode,
    latestActivity,
    _heavyUpdate, // eslint-disable-line no-unused-vars
    orderProgress
  ) => {
    let remainingSetupMs = 0;

    if (currentWorkOrderOp) {
      if (latestActivityMode === 'setup') {
        const timeInSetup = moment
          .duration(moment().diff(latestActivity.start))
          .valueOf();

        remainingSetupMs = Math.max(
          (currentWorkOrderOp.setupTimeMs || 0) - timeInSetup,
          0
        );
      }
      const { cycleTimeMs, finishQuantity } = currentWorkOrderOp;

      const unitDuration = getUnitDuration(latestActivitySet, cycleTimeMs || 0);

      if (!unitDuration) {
        return null;
      }

      // Use real-time parts count (includes both work order and labor scope parts)
      const partsRemaining = Math.max(
        (finishQuantity || 0) - (orderProgress || 0),
        0
      );
      return Math.max(unitDuration * partsRemaining + remainingSetupMs, 0);
    }
    return null;
  }
);

const getCurrentWorkOrderOpStartAt = createSelector(
  [getCurrentWorkOrderOp],
  (currentWorkOrderOp) => {
    return currentWorkOrderOp?.startAt;
  }
);

export {
  getCurrentWorkOrderOp,
  getCurrentWorkOrderOpTotalGoodParts,
  getCurrentWorkOrderOpStartAt,
  getCurrentWorkOrderOpTimeToComplete,
  getCurrentWorkOrderOpCompletionDeltaText,
  getCurrentWorkOrderOpCompletionDelta,
};
